import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordersApi, type UpdateOrderStatusDto, type RefundOrderDto } from '../api/orders.api';
import type { Order } from '../types';

const PAGINATED_STATUSES = ['DELIVERED', 'CANCELED'];
export const ORDERS_PAGE_SIZE = 25;

export const SELLER_PENDING_CONFIRMED_COUNT_KEY = [
  'orders',
  'seller',
  'pending-confirmed-count',
] as const;

export const useSellerPendingOrConfirmedCount = (enabled: boolean) => {
  return useQuery({
    queryKey: SELLER_PENDING_CONFIRMED_COUNT_KEY,
    queryFn: () => ordersApi.getSellerPendingOrConfirmedCount(),
    enabled,
    refetchInterval: 60_000,
  });
};

export const useOrders = () => {
  return useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => ordersApi.getAll(),
  });
};

export const useOrder = (id: number) => {
  return useQuery<Order>({
    queryKey: ['orders', id],
    queryFn: () => ordersApi.getById(id),
    enabled: !!id,
  });
};

export const useOrderStatusHistory = (orderId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['orders', orderId, 'status-history'],
    queryFn: () => ordersApi.getStatusHistory(orderId),
    enabled: !!orderId && enabled,
  });
};

export const useOrdersByPickupPoint = (
  pickupPointId: number,
  status?: string | null,
  searchQuery?: string,
  page: number = 1,
) => {
  const isPaginated = !!status && PAGINATED_STATUSES.includes(status);
  const search = searchQuery != null ? searchQuery.trim() : '';
  const safePage = Math.max(1, page);

  const pagedQuery = useQuery({
    queryKey: ['orders', 'pickup-point', pickupPointId, status ?? '', 'page', safePage, search],
    queryFn: () =>
      ordersApi.getByPickupPointIdPage(pickupPointId, status!, {
        limit: ORDERS_PAGE_SIZE,
        offset: (safePage - 1) * ORDERS_PAGE_SIZE,
        ...(search !== '' && { search }),
      }),
    enabled: !!pickupPointId && isPaginated,
  });

  const regularQuery = useQuery<Order[]>({
    queryKey: ['orders', 'pickup-point', pickupPointId, status ?? ''],
    queryFn: () => ordersApi.getByPickupPointId(pickupPointId, status),
    enabled: !!pickupPointId && !isPaginated,
  });

  if (isPaginated) {
    return {
      data: pagedQuery.data?.orders ?? [],
      total: pagedQuery.data?.total ?? 0,
      pageSize: ORDERS_PAGE_SIZE,
      isLoading: pagedQuery.isLoading,
      error: pagedQuery.error,
      refetch: pagedQuery.refetch,
      isFetching: pagedQuery.isFetching,
    };
  }

  const data = regularQuery.data ?? [];
  return {
    data,
    total: data.length,
    pageSize: ORDERS_PAGE_SIZE,
    isLoading: regularQuery.isLoading,
    error: regularQuery.error,
    refetch: regularQuery.refetch,
    isFetching: regularQuery.isFetching,
  };
};

export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, dto }: { orderId: number; dto: UpdateOrderStatusDto }) =>
      ordersApi.updateStatus(orderId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: SELLER_PENDING_CONFIRMED_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};

export const useUpdateOrderReady = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, files, comment }: { orderId: number; files: File[]; comment?: string }) =>
      ordersApi.updateReady(orderId, files, comment),
    // Даже если endpoint вернул ошибку, статус может успеть смениться на бэке (частичный успех).
    // Поэтому синхронизируем UI в любом случае, чтобы кнопки/модалка не работали по устаревшему статусу.
    onSettled: (_data, _error, variables) => {
      if (!variables) return;
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: SELLER_PENDING_CONFIRMED_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};

export const useRefundOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, dto }: { orderId: number; dto?: RefundOrderDto }) =>
      ordersApi.refund(orderId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: SELLER_PENDING_CONFIRMED_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};
